import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { Contract, Milestone, MilestoneDeliveryStatus, ProjectFundingContract, ProjectFundingMilestone } from '../types';
import { effectiveTimelineDeliveryStatus, timelineDeliveryPresentation } from '../utils/timelineDeliveryPresentation';
import { timelineFundingMilestoneIds, timelineFundingNeedOre } from '../utils/timelineFunding';
import { canSignContract } from '../utils/contractSigningAvailability';
import { needsPersonDraftReview } from '../utils/personTodos';
import Modal from './Modal';
import MilestoneReplacementNotice from './MilestoneReplacementNotice';
import { isReplacedMilestone, isMilestoneSettledForClosure, replacementLabel } from '../utils/milestoneHistory';
import TimelineNodeTooltip from './TimelineNodeTooltip';
import { FlowGraphOverlay, FlowGraphViewport, FlowGraphZoomControls, useFlowGraphZoom } from './FlowGraphViewport';
import type { TimelineAgreementContext } from '../utils/combinedAgreementTimeline';
import { agreementVariationNumber } from '../utils/agreementIdentity';
import { payoutAvailabilityMessage } from '../utils/payoutAvailability';
import { CaseSteps, type CaseStep } from './ObjectionCase';

interface MilestoneFlowTimelineProps {
  contract: Contract;
  combinedRoot?: Contract;
  milestoneContexts?: Record<string, TimelineAgreementContext>;
  preserveMilestoneOrder?: boolean;
  historyOnly?: boolean;
  contractSteps: FlowSubstep[];
  contractState?: FlowStepState;
  closingLabel?: string;
  emptyPhase?: { label: string; state: FlowStepState };
  emptyPlanComplete?: boolean;
  milestones: Milestone[];
  milestonePhases?: Record<string, {
    state: FlowStepState;
    steps: FlowSubstep[];
    label?: string;
    deliveryStatus?: MilestoneDeliveryStatus;
  }>;
  parentContract?: Contract | null;
  projectCompleted?: boolean;
  projectExternallyDisputed?: boolean;
  onManageStarts?: (milestone: Milestone) => void;
  canManageStarts?: boolean;
  isMilestoneReadOnly?: (milestone: Milestone) => boolean;
  inferSequentialOrder?: boolean;
  workflowStatusSummary?: React.ReactNode;
  viewerRole?: 'company' | 'person';
  fundingMilestones?: ProjectFundingMilestone[];
  fundingContract?: ProjectFundingContract;
  onPayContract?: () => void;
  onSignContract?: (contract: Contract) => void;
  /** Company side: opens a contract the customer sent back in its editor. */
  onReviseContract?: (contract: Contract) => void;
  /** Stages waiting for the viewer (e.g. to attest); drawn with a pulsing
   * red outline together with a contract the viewer can sign. */
  actionMilestoneIds?: ReadonlySet<string>;
  /** Inside a contract frame, which already names the contract: show a plain
   * "Tidslinje" heading with the contract type instead. */
  framed?: boolean;
  contractSigningDisabled?: boolean;
  renderMilestoneActions?: (
    milestone: Milestone,
    closeDetails: () => void
  ) => React.ReactNode;
}

type FlowStepState = 'done' | 'current' | 'pending' | 'warning' | 'error' | 'closed';

/** The stage the timeline opens on: one waiting for the viewer, else one with a
 * problem, else the one underway. */
const ACTIVE_NODE_SELECTORS = [
  '[data-milestone-flow-node][data-needs-action]',
  '[data-milestone-flow-node][data-state="error"]',
  '[data-milestone-flow-node][data-state="warning"]',
  '[data-milestone-flow-node][data-state="current"]'
];

type FlowSubstep = {
  key: string;
  label: string;
  state: FlowStepState;
};

type GraphMilestone = {
  milestone: Milestone;
  predecessorIds: string[];
};

type PositionedMilestone = {
  milestone: Milestone;
  predecessorIds: string[];
  depth: number;
  x: number;
  y: number;
};

const NODE_WIDTH = 210;
const NODE_HEIGHT = 84;
const X_GAP = 78;
const Y_GAP = 28;
const LEFT = 24;
const TOP = 30;

const signedForClosure = (contract: Contract) => contract.draftStatus === 'signed_by_both' || contract.signingStatus === 'signed' || (contract.companySigned && contract.personSigned);

const isCompleted = (milestone: Milestone) =>
  milestone.companyAttestation === 'approved' &&
  milestone.personAttestation === 'approved';

const isUnderReview = (milestone: Milestone) =>
  effectiveTimelineDeliveryStatus(milestone) === 'seller_attested';

const isStarted = (milestone: Milestone) =>
  Boolean(milestone.workStartedAt) ||
  Boolean(milestone.deliveryStatus && milestone.deliveryStatus !== 'not_started') ||
  isUnderReview(milestone);

export default function MilestoneFlowTimeline({
  contract,
  combinedRoot,
  milestoneContexts,
  preserveMilestoneOrder = false,
  historyOnly = false,
  contractSteps,
  contractState,
  closingLabel,
  emptyPhase,
  emptyPlanComplete = false,
  milestones,
  milestonePhases = {},
  parentContract,
  projectCompleted = false,
  projectExternallyDisputed = false,
  onManageStarts,
  canManageStarts = false,
  isMilestoneReadOnly,
  inferSequentialOrder = false,
  workflowStatusSummary,
  viewerRole,
  fundingMilestones = [],
  fundingContract,
  onPayContract,
  onSignContract,
  onReviseContract,
  actionMilestoneIds,
  framed = false,
  contractSigningDisabled = false,
  renderMilestoneActions
}: MilestoneFlowTimelineProps) {
  const { t, i18n } = useTranslation();
  const [hoveredNode, setHoveredNode] = useState<{ id: string; element: HTMLElement } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Whether the stage's action row holds any action; without one it says so
  // and offers Stäng (the actions come from the caller, so the row is checked).
  const actionRow = useRef<HTMLDivElement>(null);
  const [rowHasActions, setRowHasActions] = useState(true);
  const [selectedInfo, setSelectedInfo] = useState<'contract' | 'closing' | null>(null);
  // Desktop graph zoom (inline) and the fullscreen overlay, which has its own
  // zoom so closing it returns the inline graph unchanged.
  const inlineZoom = useFlowGraphZoom();
  const overlayZoom = useFlowGraphZoom();
  const [fullscreen, setFullscreen] = useState(false);
  const closeFullscreen = useCallback(() => setFullscreen(false), []);
  // Zoom is only offered when it is needed: the graph is wider than its
  // container, or the user has already zoomed and needs to get back.
  const [graphFits, setGraphFits] = useState(false);
  const sorted = useMemo(
    () => preserveMilestoneOrder ? milestones : [...milestones]
      .sort((left, right) => left.milestoneNumber - right.milestoneNumber),
    [milestones, preserveMilestoneOrder]
  );
  const graphMilestones = useMemo<GraphMilestone[]>(() => {
    if (inferSequentialOrder) {
      return sorted.map((milestone, index) => ({
        milestone,
        predecessorIds: index === 0 ? [] : [sorted[index - 1].id]
      }));
    }

    const started = sorted.filter(isStarted);
    const waiting = sorted.filter(milestone => !isStarted(milestone));
    const startedIds = new Set(started.map(milestone => milestone.id));
    const referencedStartedIds = new Set(
      started.flatMap(milestone =>
        (milestone.startPredecessorIds ?? []).filter(id => startedIds.has(id))
      )
    );
    const startedFrontier = started.filter(
      milestone => !referencedStartedIds.has(milestone.id)
    );

    return [
      ...started.map(milestone => ({
        milestone,
        predecessorIds: (milestone.startPredecessorIds ?? []).filter(id =>
          startedIds.has(id)
        )
      })),
      ...waiting.map((milestone, index) => ({
        milestone,
        predecessorIds: index === 0
          ? startedFrontier.map(candidate => candidate.id)
          : [waiting[index - 1].id]
      }))
    ];
  }, [inferSequentialOrder, sorted]);

  const availabilityFor = (milestone: Milestone) => milestone.deliveryStatus === 'payout_failed'
    ? fundingMilestones?.find(item => item.milestoneId === milestone.id)?.payoutAvailability : null;
  const substepsFor = (milestone: Milestone): FlowSubstep[] => {
    const deliveryStatus = availabilityFor(milestone) ? 'payout_processing' : milestone.deliveryStatus ?? 'not_started';
    const jointlyAttested = isCompleted(milestone);
    const workState: FlowStepState = !isStarted(milestone)
      ? 'pending'
      : !isUnderReview(milestone) && ['in_progress', 'under_resolution'].includes(deliveryStatus)
        ? 'current'
        : 'done';
    const attestationState: FlowStepState = (() => {
      if (deliveryStatus === 'disputed') return 'warning';
      if (['conflict', 'external_dispute'].includes(deliveryStatus)) return 'error';
      if (isUnderReview(milestone)) return 'current';
      if (['ready_for_attestation', 'ready_for_reattestation', 'seller_attested'].includes(deliveryStatus)) {
        return 'current';
      }
      if (jointlyAttested || ['jointly_attested', 'payout_processing', 'payout_failed', 'paid'].includes(deliveryStatus)) {
        return 'done';
      }
      return 'pending';
    })();
    const noPayment = (milestone.payoutAmountAfterCredits ?? milestone.afterRotAmount) <= 0;
    const payoutState: FlowStepState = deliveryStatus === 'paid' || (noPayment && jointlyAttested)
      ? 'done'
      : deliveryStatus === 'payout_failed'
        ? 'error'
        : jointlyAttested || ['jointly_attested', 'payout_processing'].includes(deliveryStatus)
          ? 'current'
          : 'pending';
    return [
      {
        key: 'work',
        label: deliveryStatus === 'under_resolution'
          ? t('contractTimeline.beingRemedied')
          : t('contractTimeline.work'),
        state: workState
      },
      {
        key: 'attestation',
        label: deliveryStatus === 'external_dispute'
          ? t('contractTimeline.dispute')
          : deliveryStatus === 'disputed'
            ? t('contractTimeline.disputed')
            : deliveryStatus === 'conflict'
              ? t('contractTimeline.conflict')
              : t('contractTimeline.attestation'),
        state: attestationState
      },
      {
        key: 'payout',
        label: t(noPayment ? 'contractTimeline.noPayoutRequired' : 'contractTimeline.payout'),
        state: payoutState
      }
    ];
  };

  const layout = useMemo(() => {
    const byId = new Map(
      graphMilestones.map(item => [item.milestone.id, item])
    );
    const depths = new Map<string, number>();
    const depthFor = (item: GraphMilestone, visiting = new Set<string>()): number => {
      const cached = depths.get(item.milestone.id);
      if (cached) return cached;
      if (visiting.has(item.milestone.id)) return 1;
      const nextVisiting = new Set(visiting).add(item.milestone.id);
      const parents = item.predecessorIds
        .map(id => byId.get(id))
        .filter((candidate): candidate is GraphMilestone => Boolean(candidate));
      const depth = parents.length
        ? Math.max(...parents.map(parent => depthFor(parent, nextVisiting))) + 1
        : 1;
      depths.set(item.milestone.id, depth);
      return depth;
    };
    const rowsByDepth = new Map<number, GraphMilestone[]>();
    graphMilestones.forEach(item => {
      const depth = depthFor(item);
      rowsByDepth.set(depth, [...(rowsByDepth.get(depth) ?? []), item]);
    });
    const positioned: PositionedMilestone[] = [];
    const groupHeightFor = (items: GraphMilestone[]) =>
      items.length * NODE_HEIGHT +
      Math.max(0, items.length - 1) * Y_GAP;
    const contentHeight = Math.max(NODE_HEIGHT, ...[...rowsByDepth.values()].map(groupHeightFor));
    rowsByDepth.forEach((items, depth) => {
      const groupHeight = groupHeightFor(items);
      const groupTop = TOP + (contentHeight - groupHeight) / 2;
      let nextY = groupTop;
      items.forEach(item => {
        positioned.push({
          ...item,
          depth,
          x: LEFT + depth * (NODE_WIDTH + X_GAP),
          y: nextY
        });
        nextY += NODE_HEIGHT + Y_GAP;
      });
    });
    const maxDepth = Math.max(1, ...positioned.map(item => item.depth));
    const height = TOP * 2 + contentHeight;
    const centerY = height / 2 - NODE_HEIGHT / 2;
    const endX = LEFT + (maxDepth + 1) * (NODE_WIDTH + X_GAP);
    const width = endX + NODE_WIDTH + LEFT;
    return { positioned, height, centerY, endX, width };
  }, [graphMilestones]);

  const positionById = new Map(layout.positioned.map(item => [item.milestone.id, item]));
  const referencedIds = new Set(
    graphMilestones.flatMap(item => item.predecessorIds)
  );
  const graphFrontier = graphMilestones.filter(
    item => !referencedIds.has(item.milestone.id)
  );
  const activeItems = sorted.filter(milestone => !isReplacedMilestone(milestone));
  const historyCount = sorted.length - activeItems.length;
  const hasStageHistory = historyCount > 0 || sorted.some(milestone => milestone.continuedBy);
  const stageItems = activeItems.filter(milestone => milestone.itemType !== 'variation_price_adjustment');
  const adjustments = activeItems.filter(milestone => milestone.itemType === 'variation_price_adjustment');
  const attestedCount = stageItems.filter(isCompleted).length;
  const paidCount = stageItems.filter(milestone => milestone.deliveryStatus === 'paid').length;
  const allStagesPaid = emptyPlanComplete || (sorted.length > 0 && sorted.every(isMilestoneSettledForClosure));
  const closureFunded = contract.versionStatus === 'superseded' || timelineFundingNeedOre(contract, fundingContract) <= 0;
  const graphComplete = !projectExternallyDisputed && (projectCompleted || (signedForClosure(contract) && closureFunded && allStagesPaid));
  const phaseState = (milestone: Milestone): FlowStepState => milestoneContexts?.[milestone.id]?.pending ? 'pending' : availabilityFor(milestone) ? 'current' : milestonePhases[milestone.id]?.state ??
    (isCompleted(milestone) ? 'done' : isStarted(milestone) ? 'current' : 'pending');
  const pendingAgreement = (milestone: Milestone) => milestoneContexts?.[milestone.id]?.pending
    ? milestoneContexts[milestone.id].contract : undefined;
  const pendingStageStatus = (agreement: Contract) => t(
    agreement.companySigned && agreement.personSigned ? 'combinedTimeline.awaitingActivation'
      : canSignContract(agreement, viewerRole) ? 'combinedTimeline.awaitingYourSignature'
        : agreement.companySigned ? 'combinedTimeline.awaitingCustomerSignature'
          : 'combinedTimeline.awaitingCompanySignature'
  );
  const presentationFor = (milestone: Milestone) => timelineDeliveryPresentation(
    availabilityFor(milestone) ? 'payout_processing' : milestonePhases[milestone.id]?.deliveryStatus ?? effectiveTimelineDeliveryStatus(milestone) ??
      (isStarted(milestone) ? 'in_progress' : 'not_started'), t
  );
  const statusColor = (milestone: Milestone) => isReplacedMilestone(milestone) || phaseState(milestone) === 'closed' ? '#6c757d' : pendingAgreement(milestone) ? 'var(--milestone-flow-pending)' : presentationFor(milestone).color;
  const closingState: FlowStepState = projectExternallyDisputed ? 'closed' : graphComplete ? 'done' : 'pending';
  const edgePath = (fromX: number, fromY: number, toX: number, toY: number) => {
    const bendX = fromX + Math.max(34, (toX - fromX) / 2);
    return `M ${fromX} ${fromY} H ${bendX} V ${toY} H ${toX}`;
  };
  const locale = i18n.language === 'en' ? 'en-GB' : 'sv-SE';
  const formatStart = (value?: string | null) => value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    : null;
  const formatDate = (value?: string | null) => value
    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(value))
    : null;
  const formatMoney = (amountOre: number) => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'SEK',
    maximumFractionDigits: 0
  }).format(amountOre / 100);
  const stageStatus = (milestone: Milestone) => {
    const pending = pendingAgreement(milestone);
    if (pending) return pendingStageStatus(pending);
    if (isReplacedMilestone(milestone)) return replacementLabel(milestone, t);
    if (phaseState(milestone) === 'closed') return t('milestoneStarts.statusClosed');
    return presentationFor(milestone).cardLabel;
  };
  // The same status as the card's colour, as a pill in the objection design
  // (ClickUp 869fm6qwf): its variant and icon.
  const stageStatusPill = (milestone: Milestone): { tone: string; icon: string } => {
    if (pendingAgreement(milestone)) return { tone: 'disputed', icon: 'edit-3' };
    if (isReplacedMilestone(milestone) || phaseState(milestone) === 'closed') return { tone: 'waiting', icon: 'archive' };
    const presentation = presentationFor(milestone);
    const tone = ({
      not_started: 'waiting',
      in_progress: 'attest',
      ready_for_attestation: 'attest',
      ready_for_reattestation: 'attest',
      seller_attested: 'attest',
      jointly_attested: 'approved',
      paid: 'approved',
      payout_processing: 'disputed',
      disputed: 'disputed',
      under_resolution: 'disputed',
      payout_failed: 'conflict',
      conflict: 'conflict',
      external_dispute: 'conflict'
    } as Record<string, string>)[presentation.status] ?? 'waiting';
    return { tone, icon: presentation.icon };
  };
  const stageCardStatus = (milestone: Milestone) => isReplacedMilestone(milestone) && !pendingAgreement(milestone)
    ? t('contractTimeline.variation') : stageStatus(milestone);
  const amendmentIcon = (milestone: Milestone) => milestone.stageReplacement?.kind === 'removed' ? 'archive' : 'edit-2';
  const stageLabel = (milestone: Milestone) => milestonePhases[milestone.id]?.label ?? (
    milestone.itemType === 'variation_price_adjustment'
      ? t(milestone.amount === 0 ? 'contractTimeline.variation' : 'contractTimeline.variationPriceChange')
      : t('milestoneStarts.stageNumber', { number: milestone.milestoneNumber })
  );
  // Cards name the stage by its description; ÄTA price changes and stages
  // without a description keep their label.
  const renderStageTitle = (milestone: Milestone) => milestone.itemType === 'variation_price_adjustment' || !milestone.description?.trim()
    ? stageLabel(milestone)
    : milestone.description;
  const stageDetailsLabel = (milestone: Milestone) => milestone.itemType === 'variation_price_adjustment'
    ? stageLabel(milestone)
    : t('milestoneStarts.openStageDetails', { number: milestone.milestoneNumber });
  const signed = contract.draftStatus === 'signed_by_both' || contract.signingStatus === 'signed' ||
    (contract.companySigned && contract.personSigned);
  const contractStatus = contractState === 'closed' ? t('milestoneStarts.statusClosed')
    : signed ? t('milestoneStarts.contractSigned')
      : contract.companySigned && !contract.personSigned ? t('contracts.draftStatus.signed_by_company')
      : contract.personSigned && !contract.companySigned ? t('contracts.draftStatus.signed_by_person')
      : contractSteps.find(step => ['error', 'warning', 'current'].includes(step.state))?.label ?? t('contractTimeline.draft');
  const endLabel = closingLabel ?? (projectExternallyDisputed
    ? t('contractTimeline.closedExternalDispute') : t('contractTimeline.closing'));
  const typeLabel = contract.stageVariation || contract.contractType === 'variation'
    ? t('contracts.types.variation')
    : t('contracts.types.fixed');
  const variationNumber = agreementVariationNumber(contract);
  const nodeLabel = !combinedRoot && variationNumber !== null
    ? t('variationWizard.originVariation', { number: variationNumber })
    : t('contractTimeline.contract');
  const identityLabel = variationNumber !== null
    ? parentContract
      ? t('variationWizard.amendmentContract', { number: variationNumber, contract: parentContract.contractNumber })
      : t('variationWizard.originVariation', { number: variationNumber })
    : `${t('contractTimeline.contract')} #${contract.contractNumber}`;

  const substepColor = (state: FlowStepState) => {
    if (state === 'done') return '#2e9d59';
    if (state === 'current') return '#3454d1';
    if (state === 'warning') return '#f0ad4e';
    if (state === 'error') return '#dc3545';
    if (state === 'closed') return '#6c757d';
    return '#adb5bd';
  };

  const fundingNeedOre = projectCompleted || projectExternallyDisputed || contract.versionStatus === 'superseded'
    ? 0 : timelineFundingNeedOre(contract, fundingContract);
  const awaitingFundingIds = milestoneContexts
    ? new Set([...new Map(Object.values(milestoneContexts).map(context => [context.contract.id, context])).values()]
      .flatMap(context => [...timelineFundingMilestoneIds(
        activeItems.filter(stage => milestoneContexts[stage.id]?.contract.id === context.contract.id),
        context.pending ? fundingNeedOre : context.fundingNeedOre
      )]))
    : timelineFundingMilestoneIds(activeItems.map(milestone => ({
    ...milestone,
    deliveryStatus: milestonePhases[milestone.id]?.deliveryStatus ?? milestone.deliveryStatus
  })), fundingNeedOre);
  const fundingLabel = t('contractTimeline.awaitingFunding');
  // In the stage dialog the funding need is the money box (ClickUp 869fm6qwf).
  const renderFundingDetails = (inCase = false) => inCase ? (
    <div className="objection-money is-funding" data-flow-funding-details>
      <i className="feather-alert-circle" aria-hidden="true"></i>
      <div>
        <div className="fw-semibold">{fundingLabel}</div>
        <div className="text-explain">{t('contractTimeline.awaitingFundingHint', { amount: formatMoney(fundingNeedOre) })}</div>
      </div>
    </div>
  ) : (
    <div className="alert alert-warning small py-2 mt-2 mb-2" data-flow-funding-details>
      <strong>{fundingLabel}</strong>
      <div className="text-explain">{t('contractTimeline.awaitingFundingHint', { amount: formatMoney(fundingNeedOre) })}</div>
    </div>
  );

  const renderSteps = (steps: FlowSubstep[], compact = false, milestoneId?: string) => (
    <div
      className={`flow-substeps ${compact ? 'is-compact' : ''}`}
      data-milestone-flow-substeps={milestoneId}
    >
      {steps.map(step => (
        <div
          key={step.key}
          className="flow-substep border rounded-2 bg-white px-2 py-1"
          data-flow-substep={step.key}
          data-state={step.state}
        >
          <span className="d-flex align-items-center gap-1">
            <span
              className="rounded-circle d-inline-block flex-shrink-0"
              aria-hidden="true"
              style={{ width: 7, height: 7, backgroundColor: substepColor(step.state) }}
            />
            <span className="flow-substep-label">
              {step.label}
            </span>
          </span>
        </div>
      ))}
    </div>
  );

  const renderSubsteps = (milestone: Milestone, compact = false) =>
    renderSteps(pendingAgreement(milestone) ? contractSteps : milestonePhases[milestone.id]?.steps ?? substepsFor(milestone), compact, milestone.id);
  const signedOn = (value?: string | null) => value
    ? new Date(value).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'sv-SE', { day: 'numeric', month: 'short' })
    : '';
  // Who signed and when moved here from the card (ClickUp 869fj8pj7).
  const renderSignatures = () => (
    <div className="flow-signatures small mb-2" data-flow-signatures>
      {([
        ['company', contract.companySigned, contract.companySignedAt, 'signedByCompanyOn', 'companyNotSigned'],
        ['person', contract.personSigned, contract.personSignedAt, 'signedByPersonOn', 'personNotSigned']
      ] as const).map(([side, done, at, signedKey, missingKey]) => (
        <div key={side} className={`flow-signature ${done ? 'is-signed' : ''}`} data-flow-signature={side}>
          <i className={`feather-${done ? 'check-circle' : 'clock'}`} aria-hidden="true"></i>
          {done ? t(`contractTimeline.${signedKey}`, { date: signedOn(at) }).trim() : t(`contractTimeline.${missingKey}`)}
        </div>
      ))}
    </div>
  );
  const renderContractDetails = (compact = false) => (
    <>
      {renderSignatures()}
      <div className="small fw-semibold mb-2">{t('milestoneStarts.substatuses')}</div>
      {renderSteps(contractSteps, compact)}
      {fundingNeedOre > 0 && renderFundingDetails()}
    </>
  );
  const renderClosingDetails = () => (
    <div className="small" data-flow-closing-summary>
      {hasStageHistory && <div className="mb-2">
        <div>{t('milestoneHistory.closingRemaining', { completed: activeItems.filter(isMilestoneSettledForClosure).length, total: activeItems.length })}</div>
        <div>{t('milestoneHistory.excludedCount', { count: historyCount })}</div>
      </div>}
      <p className="mb-2">
        {projectExternallyDisputed
          ? t('contractTimeline.closingExternalDispute')
          : projectCompleted
            ? t('contractTimeline.closingProjectClosed')
            : hasStageHistory
              ? t(graphComplete ? 'milestoneHistory.closingComplete' : 'milestoneHistory.closingRequirements')
            : allStagesPaid
              ? t(adjustments.length ? 'contractTimeline.closingCompleteWithAdjustments' : 'contractTimeline.closingComplete')
              : sorted.length === 0
                ? t('contractTimeline.closingMissingPlan')
                : t(adjustments.length ? 'contractTimeline.closingRequirementsWithAdjustments' : 'contractTimeline.closingRequirements')}
      </p>
      {!hasStageHistory && stageItems.length > 0 && (
        <div className="d-grid gap-1">
          <div className="border rounded-2 px-2 py-1">
            {t('contractTimeline.closingAttested', { completed: attestedCount, total: stageItems.length })}
          </div>
          <div className="border rounded-2 px-2 py-1">
            {t('contractTimeline.closingPaid', { completed: paidCount, total: stageItems.length })}
          </div>
        </div>
      )}
      {!hasStageHistory && adjustments.length > 0 && (
        <div className="d-grid gap-1 mt-1">
          <div className="border rounded-2 px-2 py-1">
            {t('contractTimeline.closingAdjustmentsAttested', { completed: adjustments.filter(isCompleted).length, total: adjustments.length })}
          </div>
          <div className="border rounded-2 px-2 py-1">
            {t('contractTimeline.closingAdjustmentsSettled', { completed: adjustments.filter(item => item.deliveryStatus === 'paid').length, total: adjustments.length })}
          </div>
        </div>
      )}
    </div>
  );
  const tooltipId = `flow-tooltip-${contract.id}`;
  const showTooltip = Boolean(hoveredNode && !selectedId && !selectedInfo);
  const hoverProps = (id: string): React.HTMLAttributes<HTMLElement> => ({
    'aria-describedby': showTooltip && hoveredNode?.id === id ? tooltipId : undefined,
    onMouseEnter: event => {
      if (!document.querySelector('.modal.d-block')) setHoveredNode({ id, element: event.currentTarget });
    },
    onMouseLeave: () => setHoveredNode(null),
    onFocus: event => {
      if (!document.querySelector('.modal.d-block')) setHoveredNode({ id, element: event.currentTarget });
    },
    onBlur: () => setHoveredNode(null)
  });
  const hoveredMilestone = sorted.find(milestone => milestone.id === hoveredNode?.id);
  const pingNotice = viewerRole === 'person' && contract.projectId &&
    fundingContract?.contractId === contract.id && fundingContract.paymentProvider === 'ping' &&
    fundingContract.paymentId ? fundingContract : null;
  const noticePaid = pingNotice != null && pingNotice.remainingFundingNeedOre <= 0;
  const canPayContract = viewerRole === 'person' &&
    !pingNotice &&
    !projectCompleted && !projectExternallyDisputed &&
    fundingContract?.contractId === contract.id && fundingContract.signed &&
    (fundingContract.canPay || fundingContract.canResumePayment) && Boolean(onPayContract);
  const canOpenSigning = !projectCompleted && !projectExternallyDisputed &&
    !contractSigningDisabled && Boolean(onSignContract) && canSignContract(contract, viewerRole);
  // The customer sent the contract back: it is the company's turn to change it
  // and send a new draft (ClickUp 869fftzmh).
  const companyMustRevise = !historyOnly && viewerRole === 'company' && contract.draftStatus === 'draft_rejected' &&
    !signed && !projectCompleted && !projectExternallyDisputed && Boolean(onReviseContract) &&
    ['current', 'proposed'].includes(contract.versionStatus ?? 'current');
  // The company sent a (changed) draft: the customer reviews and signs it.
  // Every round back and forth swaps whose turn it is (ClickUp 869fg9feb).
  const personMustReview = !historyOnly && viewerRole === 'person' && !canOpenSigning && needsPersonDraftReview(contract) &&
    !projectCompleted && !projectExternallyDisputed && !contractSigningDisabled && Boolean(onSignContract);
  // A draft out with the customer is the customer's turn, even though the
  // company may already sign it: the company waits, without the pulse.
  const draftWithCustomer = viewerRole === 'company' && contract.draftStatus === 'draft_sent';
  const contractNeedsAction = (!historyOnly && canOpenSigning && !draftWithCustomer) || companyMustRevise || personMustReview;
  // The company's side while the customer has the contract: waiting, no pulse.
  const companyAwaitsCustomer = !historyOnly && viewerRole === 'company' && !signed && !contractNeedsAction &&
    ['draft_sent', 'signed_by_company'].includes(contract.draftStatus ?? '') &&
    ['current', 'proposed'].includes(contract.versionStatus ?? 'current');
  // The customer signed or sent the draft back: the company's turn, no pulse.
  const personAwaitsCompany = !historyOnly && viewerRole === 'person' && !signed && !contractNeedsAction &&
    contractState !== 'closed' && ['current', 'proposed'].includes(contract.versionStatus ?? 'current') &&
    ((contract.personSigned && !contract.companySigned) || contract.draftStatus === 'draft_rejected');
  // The contract card carries exactly one status label (ClickUp 869fj8pj7):
  // whose turn it is first, then funding, then the contract's own status.
  const contractLabel: { text: string; tone: 'turn' | 'waiting' | 'funding' | 'done' | 'closed'; testId?: string } =
    contractNeedsAction ? { text: t('contractTimeline.yourTurn'), tone: 'turn', testId: 'contract-flow-your-turn' }
      : companyAwaitsCustomer ? { text: t('contractTimeline.waitingForCustomer'), tone: 'waiting', testId: 'contract-flow-waiting' }
      : personAwaitsCompany ? { text: t('contractTimeline.waitingForCompany'), tone: 'waiting', testId: 'contract-flow-waiting' }
      : fundingNeedOre > 0 ? { text: fundingLabel, tone: 'funding' }
      : { text: contractStatus, tone: contractState === 'closed' ? 'closed' : signed ? 'done' : 'waiting' };
  const renderContractLabel = () => (
    <span className={`flow-node-label on-dark is-${contractLabel.tone}`} data-testid={contractLabel.testId}
      data-flow-contract-label={contractLabel.tone}>{contractLabel.text}</span>
  );
  const renderClosingLabel = () => (
    <span className={`flow-node-label ${graphComplete ? 'on-dark is-done-solid' : 'is-neutral'}`} data-flow-closing-label>
      {graphComplete ? t('contractTimeline.closingDone')
        : projectCompleted || projectExternallyDisputed ? t('milestoneStarts.statusClosed') : t('contractTimeline.closingWaiting')}
    </span>
  );
  const needsAction = (milestone: Milestone) => !historyOnly && (pendingAgreement(milestone)
    ? !contractSigningDisabled && !!onSignContract && canSignContract(pendingAgreement(milestone)!, viewerRole)
    : !!actionMilestoneIds?.has(milestone.id));
  const withAction = (label: string, active: boolean) => active ? `${label}. ${t('contractTimeline.needsAction')}` : label;

  const renderAmendmentBadge = (milestone: Milestone) => {
    const agreement = milestoneContexts?.[milestone.id]?.contract;
    return agreement?.stageVariation && !isReplacedMilestone(milestone)
      ? <span className="badge bg-light text-secondary flex-shrink-0" data-stage-amendment={agreement.id}>
        {t('combinedTimeline.amendment', { number: agreementVariationNumber(agreement) })}
      </span> : null;
  };
  // The stage's status label; funding is marked beside it, never instead of
  // the work status (it does not change the stage's work or attestation).
  // The graph's fixed-size cards mark funding with an icon chip (the text is
  // kept for screen readers and as a tooltip); the stacked mobile cards spell it out.
  const renderStageLabel = (milestone: Milestone, compact = false) => (
    <span className="flow-node-labels">
      <span className="flow-node-label is-tinted" data-flow-stage-label={phaseState(milestone)} style={{ color: statusColor(milestone) }}>
        {isReplacedMilestone(milestone) && !pendingAgreement(milestone) && <i className={`feather-${amendmentIcon(milestone)} me-1`} aria-hidden="true" />}
        {stageCardStatus(milestone)}
      </span>
      {awaitingFundingIds.has(milestone.id) && (compact
        ? <span className="flow-node-label is-funding is-icon" data-flow-funding-label title={fundingLabel}>
          <i className="feather-credit-card" aria-hidden="true" /><span className="visually-hidden">{fundingLabel}</span>
        </span>
        : <span className="flow-node-label is-funding" data-flow-funding-label>{fundingLabel}</span>)}
      {renderAmendmentBadge(milestone)}
    </span>
  );
  const renderStageCard = (item: PositionedMilestone) => {
    const { milestone, x, y } = item;
    const color = statusColor(milestone);
    return (
      <button
        type="button"
        key={milestone.id}
        className={`position-absolute rounded-3 shadow-sm p-0 text-start ${isReplacedMilestone(milestone) ? 'bg-light text-secondary' : 'bg-white'}`}
        data-milestone-flow-node={milestone.id}
        data-replaced={isReplacedMilestone(milestone) || undefined}
        data-state={phaseState(milestone)}
        data-awaiting-funding={awaitingFundingIds.has(milestone.id) || undefined}
        data-awaiting-amendment={pendingAgreement(milestone)?.id}
        data-needs-action={needsAction(milestone) || undefined}
        aria-haspopup="dialog"
        aria-expanded={selectedId === milestone.id}
        aria-label={withAction(stageDetailsLabel(milestone), needsAction(milestone))}
        onClick={() => setSelectedId(milestone.id)}
        style={{
          left: x,
          top: y,
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
          border: `1px solid ${color}`,
          borderLeft: `5px solid ${color}`,
          zIndex: 2
        }}
        data-flow-predecessors={item.predecessorIds.join(',')}
        {...hoverProps(milestone.id)}
      >
        {/* Tight right padding: the chevron sits at the edge so the text keeps its width. */}
        <div className="d-flex align-items-center gap-2 ps-3 pe-1" style={{ height: NODE_HEIGHT }}>
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 text-white"
            style={{ width: 32, height: 32, backgroundColor: color }}
          >
            <i
              className={`feather-${isReplacedMilestone(milestone) ? amendmentIcon(milestone) : pendingAgreement(milestone) ? 'edit-3' : phaseState(milestone) === 'closed' ? 'x' : presentationFor(milestone).cardIcon}`}
              aria-hidden="true"
            ></i>
          </span>
          <span className="overflow-hidden">
            <span className={`fw-semibold d-block text-truncate ${milestone.stageReplacement?.kind === 'removed' ? 'text-decoration-line-through' : ''}`}>
              {renderStageTitle(milestone)}
            </span>
            {renderStageLabel(milestone, true)}
          </span>
          <i
            className="feather-chevron-right small text-muted flex-shrink-0 ms-auto milestone-flow-node-chevron"
            aria-hidden="true"
          ></i>
        </div>
      </button>
    );
  };

  const selectedMilestone = sorted.find(milestone => milestone.id === selectedId) ?? null;
  const selectedPendingAgreement = selectedMilestone ? pendingAgreement(selectedMilestone) : undefined;
  const canSignSelectedAgreement = !!selectedPendingAgreement && !historyOnly && !projectCompleted && !projectExternallyDisputed &&
    !contractSigningDisabled && !!onSignContract && canSignContract(selectedPendingAgreement, viewerRole);
  const canPaySelectedAgreement = !!selectedPendingAgreement && !historyOnly &&
    selectedPendingAgreement.id === contract.id && canPayContract;
  const selectedFunding = selectedMilestone
    ? fundingMilestones.find(item => item.milestoneId === selectedMilestone.id) ?? null
    : null;
  // The stage dialog in the objection modal's design (ClickUp 869fm6qwf).
  const selectedAgreement = (selectedMilestone && milestoneContexts?.[selectedMilestone.id]?.contract) || contract;
  const selectedAgreementNumber = agreementVariationNumber(selectedAgreement);
  const selectedIsAdjustment = selectedMilestone?.itemType === 'variation_price_adjustment';
  const selectedSubject = !selectedMilestone ? ''
    : selectedIsAdjustment ? stageLabel(selectedMilestone)
      : t('milestoneAttestation.subjectMilestoneTitle', { number: selectedMilestone.milestoneNumber });
  const selectedTitle = !selectedMilestone || selectedIsAdjustment || !selectedMilestone.description?.trim()
    ? selectedSubject
    : t('milestoneStarts.detailsTitleNamed', { number: selectedMilestone.milestoneNumber, name: selectedMilestone.description });
  const selectedKicker = t('milestoneStarts.detailsKicker', {
    agreement: selectedAgreementNumber !== null
      ? t('combinedTimeline.amendment', { number: selectedAgreementNumber })
      : t('combinedTimeline.agreement', { number: selectedAgreement.contractNumber }),
    subject: selectedSubject
  });
  // What the viewer gets or pays for the stage: the customer the stage after
  // ROT, the company the payout after the fee when it is known.
  const selectedPayoutOre = !selectedMilestone ? 0
    : selectedFunding
      ? viewerRole === 'person' ? selectedFunding.afterRotAmountOre : selectedFunding.netPayoutOre
      : (selectedMilestone.payoutAmountAfterCredits ?? selectedMilestone.afterRotAmount) * 100;
  const selectedPaidAt = selectedFunding?.payoutCompletedAt ?? selectedMilestone?.paidAt;
  // Arbete → Attestering → Utbetalning as the objection modal's step bars, with
  // the same states as the timeline's process steps and a short line under each.
  const stageCaseSteps = (milestone: Milestone): { steps: CaseStep[]; currentStep: number } => {
    const pending = Boolean(pendingAgreement(milestone));
    const source = pending ? contractSteps : availabilityFor(milestone) ? substepsFor(milestone) : milestonePhases[milestone.id]?.steps ?? substepsFor(milestone);
    const active = (state: FlowStepState) => ['current', 'warning', 'error'].includes(state);
    const noPayment = (milestone.payoutAmountAfterCredits ?? milestone.afterRotAmount) <= 0;
    const doneHints = [formatDate(milestone.workStartedAt), formatDate(milestone.jointlyAttestedAt), formatDate(selectedPaidAt)];
    const idleHints = [t('contractTimeline.notStarted'), undefined, noPayment ? undefined : t('milestoneStarts.afterApproval')];
    const steps = source.map((step, index): CaseStep => ({
      label: step.label,
      state: step.state,
      hint: active(step.state) ? t('objectionCase.steps.now')
        : pending || source.length !== 3 ? undefined
          : step.state === 'done' ? doneHints[index] ?? undefined
            : step.state === 'pending' ? idleHints[index] : undefined,
      attributes: { 'data-flow-substep': step.key, 'data-state': step.state }
    }));
    const current = source.findIndex(step => active(step.state));
    const firstOpen = source.findIndex(step => step.state !== 'done');
    return { steps, currentStep: current >= 0 ? current : firstOpen >= 0 ? firstOpen : source.length };
  };
  // What happens to the stage's money now, by role and status.
  const renderStageMoney = (milestone: Milestone) => {
    if (awaitingFundingIds.has(milestone.id)) return renderFundingDetails(true);
    if (pendingAgreement(milestone) || isReplacedMilestone(milestone) || milestone.continuedBy || phaseState(milestone) === 'closed') {
      return null;
    }
    if (selectedPayoutOre <= 0) return null;
    const amount = formatMoney(selectedPayoutOre);
    const availability = availabilityFor(milestone);
    const status = availability ? 'payout_processing' : milestonePhases[milestone.id]?.deliveryStatus ?? effectiveTimelineDeliveryStatus(milestone) ?? 'not_started';
    const person = viewerRole === 'person';
    const [icon, title, text] = status === 'paid' || selectedPaidAt
      ? ['check-circle', t('attestationCase.money.paidTitle', { amount }), null]
      : availability && !person
        ? ['clock', t('payoutAvailability.title'), payoutAvailabilityMessage(t, availability, i18n.language)]
      : status === 'payout_failed'
        ? ['alert-circle', t('milestoneStarts.money.approvedTitle', { amount }), t('milestoneStarts.money.failed')]
        : ['disputed', 'under_resolution', 'conflict', 'external_dispute'].includes(status)
          ? ['lock', t('objectionCase.money.title', { amount }), t(person ? 'objectionCase.money.person' : 'objectionCase.money.company')]
          : isCompleted(milestone) || ['jointly_attested', 'payout_processing'].includes(status)
            ? ['clock', t('milestoneStarts.money.approvedTitle', { amount }), t('attestationCase.money.approved')]
            : person
              ? ['lock', t('milestoneStarts.money.personTitle', { amount }), t('milestoneStarts.money.personText')]
              : ['lock', t('milestoneStarts.money.companyTitle', { amount }),
                t(selectedFunding ? 'milestoneStarts.money.companyTextAfterFee' : 'milestoneStarts.money.companyText')];
    return (
      <div className="objection-money" data-testid={`milestone-flow-money-${milestone.id}`}>
        <i className={`feather-${icon}`} aria-hidden="true"></i>
        <div>
          <div className="fw-semibold">{title}</div>
          {text && <div className="text-explain">{text}</div>}
        </div>
      </div>
    );
  };
  // A stage not yet started waits for the whole contract to be paid in, as the API requires.
  const startAwaitsFunding = (milestone: Milestone) => !isStarted(milestone) && fundingNeedOre > 0;
  const canChangeStart = (milestone: Milestone) => {
    if (
      historyOnly ||
      !canManageStarts ||
      !onManageStarts ||
      isMilestoneReadOnly?.(milestone) ||
      isReplacedMilestone(milestone) || milestone.continuedBy || contract.versionStatus === 'superseded' ||
      !viewerRole ||
      !signedForClosure(milestoneContexts?.[milestone.id]?.contract ?? contract) ||
      milestone.itemType === 'variation_price_adjustment' ||
      projectCompleted ||
      projectExternallyDisputed ||
      isCompleted(milestone)
    ) return false;
    if (!isStarted(milestone)) {
      return viewerRole === 'company' || !milestone.companyStartDecision;
    }
    return viewerRole === 'company' &&
      milestone.workStartedByRole === 'person' &&
      milestone.companyAttestation === 'not_attested' &&
      milestone.personAttestation === 'not_attested';
  };
  const selectedMilestoneActions = !historyOnly && selectedMilestone && !isMilestoneReadOnly?.(selectedMilestone) && !isReplacedMilestone(selectedMilestone) && !selectedMilestone.continuedBy && contract.versionStatus !== 'superseded'
    ? renderMilestoneActions?.(selectedMilestone, () => {
      setHoveredNode(null);
      setSelectedId(null);
    }) ?? null
    : null;

  useLayoutEffect(() => {
    const row = actionRow.current;
    if (!row) return;
    const hasActions = Array.from(row.querySelectorAll('button, a')).some(control => !control.closest('.modal-action-close-group'));
    setRowHasActions(previous => (previous === hasActions ? previous : hasActions));
  }, [selectedId, selectedMilestoneActions]);

  return (
    <div
      className={historyOnly ? "list-group" : "border rounded p-3 mb-3 bg-white"}
      data-testid={historyOnly ? "contract-stage-history-list" : "milestone-flow-timeline"}
      data-contract-timeline-id={historyOnly ? undefined : contract.id}
      data-history-contract-id={historyOnly ? contract.id : undefined}
    >
      {historyOnly ? sorted.map(milestone => (
        <button key={milestone.id} type="button" className="list-group-item list-group-item-action py-3 text-start"
          data-history-milestone={milestone.id} aria-haspopup="dialog" aria-expanded={selectedId === milestone.id}
          onClick={() => setSelectedId(milestone.id)}>
          <span className={`fw-semibold ${milestone.stageReplacement?.kind === 'removed' ? 'text-decoration-line-through' : ''}`}>{stageLabel(milestone)}</span>
          <span className="d-block small text-muted text-break">{milestone.description}</span>
          <span className="small">{stageStatus(milestone)}</span>
        </button>
      )) : <>
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
        <div>
          {combinedRoot ? <div className="combined-timeline-title">{t('combinedTimeline.title')}</div> : framed ? <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="combined-timeline-title">{t('contractTimeline.timelineTitle')}</span>
            <span className="badge bg-light text-secondary text-wrap text-start">
              {typeLabel}{variationNumber !== null ? ` #${variationNumber}` : ''}
            </span>
          </div> : <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge bg-dark text-wrap text-start">
              {typeLabel}{variationNumber !== null ? ` #${variationNumber}` : ''}
            </span>
            <span className="fw-semibold">
              {identityLabel}
            </span>
            <span className="small text-muted">{contract.description}</span>
          </div>}
          <div className="text-explain mt-1">{t(combinedRoot ? 'combinedTimeline.hint' : 'milestoneStarts.parallelTimelineHint')}</div>
        </div>
      </div>

      {workflowStatusSummary}

      {(() => {
        const graphCanvas = <>
          <svg
            className="position-absolute top-0 start-0"
            width={layout.width}
            height={layout.height}
            aria-hidden="true"
            style={{ overflow: 'visible', zIndex: 0 }}
          >
            {layout.positioned.flatMap(item => {
              const predecessors = item.predecessorIds
                .map(id => positionById.get(id))
                .filter((candidate): candidate is PositionedMilestone => Boolean(candidate));
              const sources = predecessors.length ? predecessors : [null];
              return sources.map((source, index) => {
                const fromX = source ? source.x + NODE_WIDTH : LEFT + NODE_WIDTH;
                const fromY = source ? source.y + NODE_HEIGHT / 2 : layout.centerY + NODE_HEIGHT / 2;
                return (
                  <path
                    key={`${item.milestone.id}-${source?.milestone.id ?? 'contract'}-${index}`}
                    data-flow-edge-from={source?.milestone.id ?? 'contract'}
                    data-flow-edge-to={item.milestone.id}
                    d={edgePath(fromX, fromY, item.x, item.y + NODE_HEIGHT / 2)}
                    fill="none"
                    stroke={source && isCompleted(source.milestone) && isStarted(item.milestone)
                      ? statusColor(source.milestone) : statusColor(item.milestone)}
                    strokeWidth="5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    opacity="0.78"
                  />
                );
              });
            })}
            {graphFrontier.map(item => {
              const source = positionById.get(item.milestone.id);
              if (!source) return null;
              return (
                <path
                  key={`end-${item.milestone.id}`}
                  data-flow-end-edge={item.milestone.id}
                  d={edgePath(
                    source.x + NODE_WIDTH,
                    source.y + NODE_HEIGHT / 2,
                    layout.endX,
                    layout.centerY + NODE_HEIGHT / 2
                  )}
                  fill="none"
                  stroke={graphComplete ? '#2e9d59' : '#adb5bd'}
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray={graphComplete ? undefined : '8 7'}
                  opacity="0.78"
                />
              );
            })}
            {sorted.length === 0 && (
              <path
                data-flow-empty-edge
                d={edgePath(LEFT + NODE_WIDTH, layout.centerY + NODE_HEIGHT / 2, layout.endX, layout.centerY + NODE_HEIGHT / 2)}
                fill="none" stroke="#adb5bd" strokeWidth="5" strokeDasharray="8 7"
              />
            )}
          </svg>

          <button
            type="button"
            className="position-absolute rounded-3 border-0 text-start text-white shadow-sm d-flex align-items-center gap-2 px-3"
            data-flow-contract-node
            data-state={contractState ?? (signed ? 'done' : 'current')}
            data-needs-action={contractNeedsAction || undefined}
            aria-label={withAction(t('contractTimeline.openContractDetails'), contractNeedsAction)}
            aria-haspopup="dialog"
            aria-expanded={selectedInfo === 'contract'}
            onClick={() => setSelectedInfo('contract')}
            {...hoverProps('contract')}
            style={{
              left: LEFT,
              top: layout.centerY,
              width: NODE_WIDTH,
              height: NODE_HEIGHT,
              background: '#18243d',
              zIndex: 2
            }}
          >
            <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white text-dark" style={{ width: 32, height: 32 }}>
              <i className="feather-file-text" aria-hidden="true"></i>
            </span>
            <span className="overflow-hidden">
              <span className="fw-semibold d-block">{nodeLabel}</span>
              {renderContractLabel()}
            </span>
            <i className="feather-chevron-right small ms-auto flex-shrink-0 milestone-flow-node-chevron" aria-hidden="true"></i>
          </button>

          {layout.positioned.map(renderStageCard)}
          {sorted.length === 0 && emptyPhase && (
            <div
              className="position-absolute rounded-3 bg-white border p-3 d-flex align-items-center gap-2"
              data-flow-empty-node
              data-state={emptyPhase.state}
              style={{ left: LEFT + NODE_WIDTH + X_GAP, top: layout.centerY, width: NODE_WIDTH, height: NODE_HEIGHT, color: substepColor(emptyPhase.state) }}
            >
              <i className={`feather-${emptyPhase.state === 'error' ? 'alert-triangle' : 'clock'}`} aria-hidden="true" />
              <span className="fw-semibold">{emptyPhase.label}</span>
            </div>
          )}

          <button
            type="button"
            className={`position-absolute rounded-3 text-start d-flex align-items-center gap-2 px-3 ${graphComplete ? 'text-white shadow-sm' : 'bg-light text-muted'}`}
            data-flow-end-node
            data-state={closingState}
            aria-label={t('contractTimeline.openClosingDetails')}
            aria-haspopup="dialog"
            aria-expanded={selectedInfo === 'closing'}
            onClick={() => setSelectedInfo('closing')}
            {...hoverProps('closing')}
            style={{
              left: layout.endX,
              top: layout.centerY,
              width: NODE_WIDTH,
              height: NODE_HEIGHT,
              backgroundColor: graphComplete ? '#2e9d59' : undefined,
              border: graphComplete ? 'none' : '1px dashed #adb5bd',
              zIndex: 2
            }}
          >
            <span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white text-dark" style={{ width: 32, height: 32 }}>
              <i className="feather-flag" aria-hidden="true"></i>
            </span>
            <span className="overflow-hidden">
              <span className="fw-semibold d-block">{endLabel}</span>
              {renderClosingLabel()}
            </span>
            <i className="feather-chevron-right small ms-auto flex-shrink-0 milestone-flow-node-chevron" aria-hidden="true"></i>
          </button>
        </>;
        const overlayTitle = combinedRoot
          ? t('combinedTimeline.title')
          : `${identityLabel} · ${contract.description}`;
        return <>
          {/* Desktop graph: zoom controls above a scroll/zoom viewport. While the
              overlay is open the canvas lives there instead, and a same-height
              placeholder keeps the page from jumping. */}
          <div className="d-none d-md-block">
            <div className="d-flex justify-content-end mb-2">
              <FlowGraphZoomControls zoom={inlineZoom} hideZoom={graphFits && inlineZoom.zoom === 1}
                onToggleFullscreen={() => { overlayZoom.setZoom(inlineZoom.zoom); setFullscreen(true); }} />
            </div>
            {fullscreen ? (
              <div className="flow-graph-placeholder rounded-3 d-flex align-items-center justify-content-center text-muted small"
                style={{ height: layout.height * inlineZoom.zoom }} data-testid="flow-graph-placeholder">
                {t('contractTimeline.zoom.shownInFullscreen')}
              </div>
            ) : (
              <FlowGraphViewport width={layout.width} height={layout.height} zoom={inlineZoom} className="pb-2" testId="flow-graph-viewport" onFitsChange={setGraphFits}
                focusSelectors={ACTIVE_NODE_SELECTORS}>
                {graphCanvas}
              </FlowGraphViewport>
            )}
          </div>
          {fullscreen && (
            <FlowGraphOverlay title={overlayTitle} zoom={overlayZoom} onClose={closeFullscreen}>
              <FlowGraphViewport width={layout.width} height={layout.height} zoom={overlayZoom} pannable fill testId="flow-graph-overlay-viewport">
                {graphCanvas}
              </FlowGraphViewport>
            </FlowGraphOverlay>
          )}
        </>;
      })()}

      <div className="d-md-none d-grid gap-2">
        <button
          type="button"
          className="rounded-3 border-0 text-start p-3 text-white d-flex align-items-center"
          data-mobile-flow-contract-node
          data-needs-action={contractNeedsAction || undefined}
          style={{ background: '#18243d' }}
          aria-label={withAction(t('contractTimeline.openContractDetails'), contractNeedsAction)}
          aria-haspopup="dialog"
          aria-expanded={selectedInfo === 'contract'}
          onClick={() => setSelectedInfo('contract')}
        >
          <span className="flex-grow-1">
            <i className="feather-file-text me-2" aria-hidden="true"></i>
            <span className="fw-semibold">{nodeLabel}</span>
            {renderContractLabel()}
          </span>
          <i className="feather-chevron-right small ms-2 flex-shrink-0 milestone-flow-node-chevron" aria-hidden="true"></i>
        </button>
        {[...layout.positioned]
          .sort((left, right) => left.depth - right.depth || left.milestone.milestoneNumber - right.milestone.milestoneNumber)
          .map(item => (
            <button
              key={item.milestone.id}
              type="button"
              className="d-flex align-items-stretch gap-2 border-0 bg-transparent p-0 text-start"
              aria-haspopup="dialog"
              aria-expanded={selectedId === item.milestone.id}
              aria-label={withAction(stageDetailsLabel(item.milestone), needsAction(item.milestone))}
              data-mobile-milestone-flow-node={item.milestone.id}
              data-state={phaseState(item.milestone)}
              data-replaced={isReplacedMilestone(item.milestone) || undefined}
              data-awaiting-funding={awaitingFundingIds.has(item.milestone.id) || undefined}
              data-awaiting-amendment={pendingAgreement(item.milestone)?.id}
              data-needs-action={needsAction(item.milestone) || undefined}
              onClick={() => setSelectedId(item.milestone.id)}
            >
              <div className="border-start border-4 ms-3" style={{ borderColor: statusColor(item.milestone) }} />
              <div className={`border rounded-3 p-3 flex-grow-1 ${isReplacedMilestone(item.milestone) ? 'bg-light text-secondary' : ''}`}>
                <div className={`fw-semibold text-break ${item.milestone.stageReplacement?.kind === 'removed' ? 'text-decoration-line-through' : ''}`}>{renderStageTitle(item.milestone)}</div>
                {renderStageLabel(item.milestone)}
                {item.milestone.workStartedByRole === 'person' && (
                  <div className="small mt-1">
                    <i className="feather-user-check me-1" aria-hidden="true"></i>
                    {t('milestoneStarts.startedByCustomer')}
                  </div>
                )}
              </div>
              <i
                className="feather-chevron-right small text-muted flex-shrink-0 align-self-center me-2 milestone-flow-node-chevron"
                aria-hidden="true"
              ></i>
            </button>
          ))}
        {sorted.length === 0 && emptyPhase && (
          <div className="border rounded-3 p-3" style={{ color: substepColor(emptyPhase.state) }}>
            {emptyPhase.label}
          </div>
        )}
        <button
          type="button"
          className={`rounded-3 border text-start p-3 d-flex align-items-center ${graphComplete ? 'text-white' : 'bg-light text-muted'}`}
          data-mobile-flow-end-node
          style={{ backgroundColor: graphComplete ? '#2e9d59' : undefined }}
          aria-label={t('contractTimeline.openClosingDetails')}
          aria-haspopup="dialog"
          aria-expanded={selectedInfo === 'closing'}
          onClick={() => setSelectedInfo('closing')}
        >
          <span className="flex-grow-1">
            <i className="feather-flag me-2" aria-hidden="true"></i>
            <span className="fw-semibold">
              {endLabel}
            </span>
            {renderClosingLabel()}
          </span>
          <i className="feather-chevron-right small ms-2 flex-shrink-0 milestone-flow-node-chevron" aria-hidden="true"></i>
        </button>
      </div>

      </>}

      {showTooltip && hoveredNode && (
        <TimelineNodeTooltip
          id={tooltipId}
          anchor={hoveredNode.element}
          onDismiss={() => setHoveredNode(null)}
        >
          {hoveredNode.id === 'contract' ? renderContractDetails(true)
            : hoveredNode.id === 'closing' ? (
              <>
                <div className="small fw-semibold mb-2">{t('contractTimeline.closing')}</div>
                {renderClosingDetails()}
              </>
            ) : hoveredMilestone ? (
              <>
                <div className="small fw-semibold mb-2">{t('milestoneStarts.substatuses')}</div>
                {isReplacedMilestone(hoveredMilestone) && <div className="small mb-2">{replacementLabel(hoveredMilestone, t)}</div>}
                {renderSubsteps(hoveredMilestone, true)}
                {awaitingFundingIds.has(hoveredMilestone.id) && (
                  <>
                    <div className="small mt-2">{hoveredMilestone.description}</div>
                    {renderFundingDetails()}
                  </>
                )}
                {hoveredMilestone.workStartedByRole === 'person' && (
                  <div className="small mt-2 pt-2 border-top">
                    <i className="feather-user-check me-1" aria-hidden="true"></i>
                    {t('milestoneStarts.startedByCustomer')}
                    {formatStart(hoveredMilestone.workStartedAt) && (
                      <span className="text-muted d-block mt-1">{formatStart(hoveredMilestone.workStartedAt)}</span>
                    )}
                  </div>
                )}
              </>
            ) : null}
        </TimelineNodeTooltip>
      )}

      {selectedInfo && (
        <Modal
          title={selectedInfo === 'contract' ? nodeLabel : t('contractTimeline.closing')}
          onClose={() => setSelectedInfo(null)}
          footer={(
            // Stäng to the left, the contract's action (sign, change, pay) to
            // the right in the same row (ClickUp 869fg9feb).
            <div className="modal-action-row" role="group" aria-label={t('milestoneStarts.availableActions')}
              data-testid={`contract-flow-actions-${contract.id}`}>
              <button type="button" className="btn btn-secondary" onClick={() => setSelectedInfo(null)}>
                {t('common.close')}
              </button>
              {selectedInfo === 'contract' && (
                <>
                {companyMustRevise && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid={`contract-flow-revise-${contract.id}`}
                    onClick={() => {
                      setHoveredNode(null);
                      setSelectedInfo(null);
                      onReviseContract?.(contract);
                    }}
                  >
                    <i className="feather-edit-3 me-2" aria-hidden="true"></i>
                    {t('contractTimeline.reviseContract')}
                  </button>
                )}
                {(canOpenSigning || personMustReview) && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid={`contract-flow-sign-${contract.id}`}
                    onClick={() => {
                      setHoveredNode(null);
                      setSelectedInfo(null);
                      onSignContract?.(contract);
                    }}
                  >
                    <i className="feather-check-square me-2" aria-hidden="true"></i>
                    {t('contractReplacement.reviewAndSign')}
                  </button>
                )}
                {canPayContract && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    data-testid={`contract-flow-pay-${contract.id}`}
                    onClick={() => {
                      setHoveredNode(null);
                      setSelectedInfo(null);
                      onPayContract?.();
                    }}
                  >
                    <i className="feather-credit-card me-2" aria-hidden="true"></i>
                    {t(fundingContract?.canResumePayment ? 'projectFunding.resumePayment' : 'projectFunding.payContract')}
                  </button>
                )}
                </>
              )}
            </div>
          )}
        >
          {selectedInfo === 'contract' ? renderContractDetails() : renderClosingDetails()}
          {selectedInfo === 'contract' && pingNotice && (
            <section className="mt-4 contract-flow-notice" aria-labelledby={`contract-notice-${contract.id}`}>
              <h6 id={`contract-notice-${contract.id}`}>{t('pingDeposit.document.title')}</h6>
              <Link
                className={noticePaid
                  ? 'project-funding-notice-link d-inline-flex align-items-center gap-2 text-decoration-underline'
                  : 'btn btn-primary project-funding-action'}
                data-testid={`contract-flow-notice-${contract.id}`}
                to={`/payment-checkout?${new URLSearchParams({
                  projectId: contract.projectId,
                  contractId: contract.id,
                  paymentId: pingNotice.paymentId!,
                  provider: 'ping',
                  method: 'bank'
                })}`}
                onClick={() => {
                  setHoveredNode(null);
                  setSelectedInfo(null);
                }}
              >
                <i className={`bi ${noticePaid ? 'bi-file-earmark-text' : 'bi-receipt me-2'}`} aria-hidden="true" />
                {t(noticePaid ? 'pingDeposit.viewPaidNotice' : 'pingDeposit.openNotice')}
              </Link>
            </section>
          )}
        </Modal>
      )}

      {selectedMilestone && (
        <Modal
          title={selectedTitle}
          renderHeader={titleId => {
            const pill = stageStatusPill(selectedMilestone);
            return (
              <div className="objection-header-main">
                <div className="objection-kicker">{selectedKicker}</div>
                <div className="objection-header-title">
                  <h2 className="modal-title" id={titleId}>{selectedTitle}</h2>
                  <span className="objection-header-amount">{formatMoney(selectedMilestone.amount * 100)}</span>
                </div>
                <span className={`objection-status-pill is-${pill.tone}`} data-testid={`milestone-flow-status-${selectedMilestone.id}`}>
                  <i className={`feather-${pill.icon}`} aria-hidden="true"></i>
                  {stageStatus(selectedMilestone)}
                </span>
              </div>
            );
          }}
          headerClassName="objection-header"
          dialogClassName="objection-dialog stage-details-dialog"
          onClose={() => setSelectedId(null)}
          size="lg"
          fullscreenBelow="sm"
          footer={(
            // The stage's actions stay in view while the details scroll (ClickUp 869fbrxwt).
            <div
              ref={actionRow}
              className="modal-action-row milestone-flow-actions"
              role="group"
              aria-label={t('milestoneStarts.availableActions')}
              data-testid={`milestone-flow-actions-${selectedMilestone.id}`}
            >
              {canChangeStart(selectedMilestone) && (startAwaitsFunding(selectedMilestone) ? (
                // The money is reserved before the work begins: shown, but not
                // startable until the contract is paid in (ClickUp 869fg0x10).
                <>
                  <button
                    type="button"
                    className="btn btn-outline-primary disabled"
                    aria-disabled="true"
                    aria-describedby={`milestone-start-funding-${selectedMilestone.id}`}
                    data-testid={`milestone-flow-start-blocked-${selectedMilestone.id}`}
                    onClick={event => event.preventDefault()}
                  >
                    <i className="feather-play-circle me-1" aria-hidden="true"></i>
                    {t('milestoneStarts.markStarted')}
                  </button>
                  <span id={`milestone-start-funding-${selectedMilestone.id}`} className="modal-action-note text-explain">
                    {t(viewerRole === 'person' ? 'milestoneStarts.startNeedsFundingPerson' : 'milestoneStarts.startNeedsFunding', {
                      amount: formatMoney(fundingNeedOre)
                    })}
                  </span>
                  {canPayContract && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      data-testid={`milestone-flow-pay-${selectedMilestone.id}`}
                      onClick={() => {
                        setHoveredNode(null);
                        setSelectedId(null);
                        onPayContract?.();
                      }}
                    >
                      <i className="feather-credit-card me-1" aria-hidden="true"></i>
                      {t(fundingContract?.canResumePayment ? 'projectFunding.resumePayment' : 'projectFunding.payContract')}
                    </button>
                  )}
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setHoveredNode(null);
                    setSelectedId(null);
                    onManageStarts?.(selectedMilestone);
                  }}
                >
                  <i className="feather-play-circle me-1" aria-hidden="true"></i>
                  {isStarted(selectedMilestone)
                    ? t('milestoneStarts.changeStart')
                    : t('milestoneStarts.markStarted')}
                </button>
              ))}
              {selectedMilestoneActions}
              {canSignSelectedAgreement && (
                <button type="button" className="btn btn-primary" data-testid={`milestone-flow-sign-${selectedMilestone.id}`}
                  onClick={() => {
                    setHoveredNode(null);
                    setSelectedId(null);
                    onSignContract?.(selectedPendingAgreement!);
                  }}>
                  <i className="feather-check-square me-2" aria-hidden="true"></i>
                  {t('contractReplacement.reviewAndSign')}
                </button>
              )}
              {canPaySelectedAgreement && (
                <button type="button" className="btn btn-primary" data-testid={`milestone-flow-pay-${selectedMilestone.id}`}
                  onClick={() => {
                    setHoveredNode(null);
                    setSelectedId(null);
                    onPayContract?.();
                  }}>
                  <i className="feather-credit-card me-2" aria-hidden="true"></i>
                  {t(fundingContract?.canResumePayment ? 'projectFunding.resumePayment' : 'projectFunding.payContract')}
                </button>
              )}
              {!rowHasActions && (
                <span className="modal-action-close-group">
                  <span className="modal-action-empty small text-muted">{t('milestoneStarts.noActions')}</span>
                  <button type="button" className="btn btn-secondary" onClick={() => setSelectedId(null)}>
                    {t('common.close')}
                  </button>
                </span>
              )}
            </div>
          )}
        >
          <div className="objection-case" data-testid={`milestone-flow-details-${selectedMilestone.id}`}>
            {(() => {
              const { steps, currentStep } = stageCaseSteps(selectedMilestone);
              return (
                <div className="objection-steps-block" data-milestone-flow-substeps={selectedMilestone.id}>
                  <CaseSteps steps={steps} currentStep={currentStep} />
                </div>
              );
            })()}

            {/* What the actions below answer: the objection or the ÄTA waiting to take effect. */}
            {selectedPendingAgreement && (
              <div className="objection-quote is-company" data-testid="milestone-pending-amendment">
                <div className="objection-quote-label">{pendingStageStatus(selectedPendingAgreement)}</div>
                <p className="mb-0">{t('combinedTimeline.pendingStageHint')}</p>
              </div>
            )}

            {(() => {
              const money = renderStageMoney(selectedMilestone) ??
                (selectedPendingAgreement && fundingNeedOre > 0 ? renderFundingDetails(true) : null);
              const reason = selectedMilestone.disputeReason;
              const disputedAt = formatDate(selectedMilestone.disputedAt);
              if (!reason && !money) return null;
              return (
                <div className={`objection-facts ${reason && money ? 'has-quote' : ''}`}>
                  {reason && (
                    <div className="objection-quote is-company" data-testid={`milestone-flow-objection-${selectedMilestone.id}`}>
                      <div className="objection-quote-label">
                        {!disputedAt
                          ? t('milestoneStarts.objectionLabel')
                          : viewerRole === 'person'
                            ? t('objectionCase.objection.personLabel', { date: disputedAt })
                            : t('objectionCase.objection.companyLabelNoName', { date: disputedAt })}
                      </div>
                      <blockquote className="mb-0">”{reason}”</blockquote>
                    </div>
                  )}
                  {money}
                </div>
              );
            })()}

            <MilestoneReplacementNotice milestone={selectedMilestone} projectId={contract.projectId}
              amendment={milestoneContexts?.[selectedMilestone.id]} className="" />

            <section aria-labelledby={`metadata-${selectedMilestone.id}`}>
              <h3 id={`metadata-${selectedMilestone.id}`} className="objection-section-title">
                {t('milestoneStarts.stageInformation')}
              </h3>
              <dl className="row g-2 mb-0 stage-details-info">
                <dt className="col-sm-5">{t('milestoneStarts.startMarkedAt')}</dt>
                <dd className="col-sm-7 mb-0">
                  {formatStart(selectedMilestone.workStartedAt) ?? t('milestoneStarts.notMarked')}
                  {selectedMilestone.workStartedByRole === 'person' && (
                    <span className="stage-details-note">{t('milestoneStarts.startedByCustomer')}</span>
                  )}
                </dd>
                <dt className="col-sm-5">{t('milestoneStarts.plannedCompletion')}</dt>
                <dd className="col-sm-7 mb-0">
                  {formatDate(selectedMilestone.endDate) ?? t('milestoneStarts.notSpecified')}
                </dd>
                <dt className="col-sm-5">
                  {selectedFunding && viewerRole !== 'person'
                    ? t('milestoneStarts.expectedPayout')
                    : t('milestoneStarts.stageAmountAfterRot')}
                </dt>
                <dd className="col-sm-7 mb-0 fw-semibold" data-testid="milestone-flow-payout-amount">
                  {formatMoney(selectedPayoutOre)}
                </dd>
                {selectedMilestone.jointlyAttestedAt && (
                  <>
                    <dt className="col-sm-5">{t('milestoneStarts.approvedAt')}</dt>
                    <dd className="col-sm-7 mb-0">{formatStart(selectedMilestone.jointlyAttestedAt)}</dd>
                  </>
                )}
                {selectedPaidAt && (
                  <>
                    <dt className="col-sm-5">{t('milestoneStarts.paidAt')}</dt>
                    <dd className="col-sm-7 mb-0">{formatStart(selectedPaidAt)}</dd>
                  </>
                )}
              </dl>
            </section>
          </div>
        </Modal>
      )}

    </div>
  );
}
